import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Building2 } from "lucide-react";
import { GOLD, NAVY } from "./ui";
import { BUSINESS } from "../../data/business/profile";

const FOOTER_LOGO =
  "https://customer-assets.emergentagent.com/job_b2ca0c7b-d03f-4a16-bd13-4c17923effbc/artifacts/i1zeqilg_4.png";

const COLUMNS = [
  {
    title: "For vognmænd",
    links: [
      { label: "Drifts- og økonomipartner", to: "/business/vognmaend" },
      { label: "Drift & backoffice", to: "/business/drift" },
      { label: "Financial controlling", to: "/business/financial-controlling" },
      { label: "Fleet management", to: "/business/fleet-management" },
      { label: "Compliance & dokumenter", to: "/business/compliance" },
      { label: "Rammeaftaler", to: "/business/rammeaftaler" },
      { label: "Vækst & næste bil", to: "/business/vaekst" },
      { label: "Beregnere", to: "/business/beregnere" },
    ],
  },
  {
    title: "Ydelser",
    links: [
      { label: "Regnskab & bogføring", to: "/business/regnskab" },
      { label: "Løn", to: "/business/loen" },
      { label: "Virksomhedsopstart", to: "/business/virksomhedsopstart" },
      { label: "Rekruttering", to: "/business/rekruttering" },
      { label: "Andre virksomheder", to: "/business/andre-virksomheder" },
      { label: "Votra Business Portal", to: "/business/portal" },
    ],
  },
  {
    title: "Virksomhed",
    links: [
      { label: "Om os", to: "/business/om-os" },
      { label: "Partnere", to: "/business/partnere" },
      { label: "Cases", to: "/business/cases" },
      { label: "Viden", to: "/business/viden" },
      { label: "Kontakt", to: "/business/kontakt" },
      { label: "Votra Danmark, kurser", to: "/kurser" },
    ],
  },
];

const BusinessFooter = ({ tagline }) => {
  return (
    <footer className="text-white" style={{ backgroundColor: NAVY }} data-testid="business-footer">
      <div className="h-[3px] w-full" style={{ backgroundColor: GOLD }} />
      <div className="max-w-[1680px] mx-auto px-4 md:px-8 lg:px-12 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8">
          {/* Brand + entitet */}
          <div className="lg:col-span-3">
            <img src={FOOTER_LOGO} alt="Votra Business" width="320" height="80" loading="lazy" className="h-16 md:h-20 w-auto object-contain mb-5" data-testid="business-footer-logo" />
            <p className="text-xs tracking-[0.2em] uppercase font-semibold mb-6" style={{ color: GOLD }}>
              {tagline || BUSINESS.oneLiner}
            </p>
            <div className="space-y-3 text-sm text-slate-400" data-testid="business-footer-entity">
              <p className="flex items-start gap-2.5">
                <Building2 className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: GOLD }} />
                <span><span className="text-slate-200">{BUSINESS.legalName}</span><br />CVR {BUSINESS.cvr}</span>
              </p>
              <p className="flex items-start gap-2.5">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: GOLD }} />
                <span>
                  <span className="text-slate-200">Registreret adresse:</span> {BUSINESS.registered.co},<br />
                  {BUSINESS.registered.street}, {BUSINESS.registered.zip} {BUSINESS.registered.city}<br />
                  <span className="text-slate-200">Kontor:</span> {BUSINESS.office.street}, {BUSINESS.office.zip} {BUSINESS.office.city}
                </span>
              </p>
              <a href={`mailto:${BUSINESS.email}`} className="flex items-center gap-2.5 hover:text-white transition-colors">
                <Mail className="h-4 w-4" style={{ color: GOLD }} /> {BUSINESS.email}
              </a>
              <a href={`tel:${BUSINESS.phoneHref}`} className="flex items-center gap-2.5 hover:text-white transition-colors">
                <Phone className="h-4 w-4" style={{ color: GOLD }} /> {BUSINESS.phone}
              </a>
            </div>
          </div>

          {COLUMNS.map((col) => (
            <div key={col.title} className="lg:col-span-3">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-4">{col.title}</p>
              <nav className="flex flex-col gap-2.5 text-sm" aria-label={col.title}>
                {col.links.map((l) => (
                  <Link key={l.to} to={l.to}
                    className="text-left text-slate-300 hover:text-white transition-colors"
                    data-testid={`footer-link-${l.to.split("/").filter(Boolean).pop()}`}>
                    {l.label}
                  </Link>
                ))}
              </nav>
            </div>
          ))}
        </div>

        <p className="mt-12 text-xs text-slate-400 max-w-3xl leading-relaxed">
          Aftalepart på disse sider er {BUSINESS.legalName}, CVR {BUSINESS.cvr}. Uddannelser og kurser
          udbydes af det selvstændige selskab Votra Danmark ApS, CVR 36486899. {BUSINESS.groupLine}{" "}
          Votra Business ApS er ikke revisionsvirksomhed og udfører ikke revision.
          Forsikringsaftaler indgås mellem kunden og forsikringsselskabet eller dennes formidler.
        </p>

        <div className="border-t border-white/10 mt-8 pt-6 flex flex-col md:flex-row md:items-center justify-between gap-4 text-slate-400 text-sm">
          <p>&copy; {new Date().getFullYear()} {BUSINESS.legalName} · CVR {BUSINESS.cvr}</p>
          <div className="flex flex-wrap items-center gap-5">
            <Link to="/business/vilkaar" className="hover:text-white transition-colors" data-testid="footer-terms-link">
              Samarbejdsvilkår
            </Link>
            <Link to="/business/privatliv" className="hover:text-white transition-colors" data-testid="footer-privacy-link">
              Privatlivspolitik
            </Link>
            <Link to="/mitvotra" className="hover:text-white transition-colors" data-testid="footer-login-link">
              Log ind
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default BusinessFooter;
